import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useNavigationType } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton, BottomSheet, IconDot3HorizontalLine, IconPersonLine, IconTrashcanLine, MenuSheetItem, TopNavigation } from "@hamlak/ui";
import { FeedbackEntry } from "@/components/FeedbackEntry";
import catSymbolUrl from "@hamlak/ui/brand/cat-symbol.svg";
import { qk } from "@/api/queryKeys";
import type { ConversationListItem } from "@/api/types";
import { platform } from "@/platform";
import { useAuthStore } from "@/stores/authStore";
import { t } from "@/i18n/ko";
import { formatRelative } from "@/lib/relativeTime";
import { useUiStore } from "@/stores/uiStore";
import { BottomTabs } from "@/components/BottomTabs";
import { EmptyState } from "@/components/EmptyState";
import { LeaveDialog } from "./LeaveDialog";
import { previewText } from "./previewText";
import { useConversationList } from "./useConversationList";
import { useForgetConversation, useLeaveConversation } from "./useLeaveConversation";
import "./conversations.css";

/** 목록을 떠날 때의 스크롤. 뒤로가기(POP)로 돌아오면 복원하고, 탭·링크로 오면 맨 위에서 시작한다 */
let savedScrollY = 0;

/** F03-01·02 대화 탭(/chats, 회원 전용). 결정 정본: docs/superpowers/specs/2026-10-24-conversation-list-design.md */
export function ConversationsPage() {
  const navigate = useNavigate();
  const list = useConversationList();
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const sheetOpen = useUiStore((s) => s.sheets.includes("conversationMenu"));
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const [menuFor, setMenuFor] = useState<ConversationListItem | null>(null);
  const [leaving, setLeaving] = useState<ConversationListItem | null>(null);
  const leave = useLeaveConversation();
  const forget = useForgetConversation();
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? "");
  const navigationType = useNavigationType();
  const restored = useRef(false);

  const enter = (item: ConversationListItem) => {
    if (!item.plot.chatAvailable) return showSnackbar(t("conversations.unavailableTap"));
    navigate(`/chat/${encodeURIComponent(item.plot.slug)}`);
  };
  const openMenu = (item: ConversationListItem) => {
    setMenuFor(item);
    openSheet("conversationMenu");
  };

  const items = list.data?.items;

  // 다른 기기·탭에서 대화하고 돌아오면 순서·미리보기가 바뀌어 있다(전역 refetchOnWindowFocus 는 꺼져 있다)
  useEffect(() => platform.app.onForeground(() => void qc.invalidateQueries({ queryKey: qk.conversationList(userId) })), [qc, userId]);
  // 레이아웃 정리 단계에서 저장한다: 일반 effect 정리는 다음 화면의 레이아웃 effect(채팅방 맨 위로 스크롤) 뒤에 돌아 0 을 저장한다
  useLayoutEffect(
    () => () => {
      savedScrollY = window.scrollY;
    },
    [],
  );
  useLayoutEffect(() => {
    if (restored.current || !items) return;
    restored.current = true;
    window.scrollTo?.(0, navigationType === "POP" ? savedScrollY : 0);
  }, [items, navigationType]);
  return (
    <main className="page" data-testid="conversations-page">
      <TopNavigation type="pageTitle" title={t("page.chats")} left={null} right={null} />
      {/* 의견 배너(F03-01·F03-02): 제목 바로 아래, 빈 목록에서도 */}
      <div className="convs__feedback">
        <FeedbackEntry from="chat_list" />
      </div>
      {list.isPending ? (
        <div className="page-placeholder" role="status">
          {t("common.loading")}
        </div>
      ) : list.isError || !items ? (
        <EmptyState message={t("conversations.loadError")} action={{ label: t("common.retry"), onClick: () => void list.refetch() }} />
      ) : items.length === 0 ? (
        <div className="convs__empty" data-testid="conversations-empty">
          <img src={catSymbolUrl} alt="" className="convs__emptyIcon" />
          <p className="convs__emptyTitle">{t("conversations.empty.title")}</p>
          <p className="convs__emptyBody">{t("conversations.empty.body")}</p>
          <ActionButton size="small" onClick={() => navigate("/home")}>
            {t("conversations.empty.cta")}
          </ActionButton>
        </div>
      ) : (
        <ul className="convs__list">
          {items.map((item) => (
            <li key={item.conversationId} className="convs__row" data-unavailable={item.plot.chatAvailable ? undefined : ""}>
              <button type="button" className="convs__main" onClick={() => enter(item)}>
                <img className="convs__avatar" src={item.plot.avatarUrl} alt="" />
                <span className="convs__text">
                  <span className="convs__top">
                    <span className="convs__name">{item.plot.name}</span>
                    <span className="convs__time">{formatRelative(item.lastActivityAt)}</span>
                  </span>
                  <span className="convs__preview">{item.plot.chatAvailable ? previewText(item.preview) : t("conversations.unavailable")}</span>
                </span>
              </button>
              <button type="button" className="convs__more" aria-label={t("conversations.more", { name: item.plot.name })} onClick={() => openMenu(item)}>
                <IconDot3HorizontalLine />
              </button>
            </li>
          ))}
        </ul>
      )}
      <BottomSheet open={sheetOpen && menuFor !== null} onClose={() => closeSheet("conversationMenu")} header={{ title: menuFor?.plot.name }} data-testid="conversation-menu-sheet">
        {menuFor?.plot.chatAvailable && (
          <MenuSheetItem
            icon={<IconPersonLine />}
            label={t("chat.menu.plot")}
            suffix="chevron"
            onClick={() => {
              closeSheet("conversationMenu");
              navigate(`/plots/${encodeURIComponent(menuFor.plot.slug)}`);
            }}
          />
        )}
        {menuFor && (
          <MenuSheetItem
            icon={<IconTrashcanLine />}
            label={t("chat.menu.leave")}
            tone="critical"
            onClick={() => {
              closeSheet("conversationMenu");
              setLeaving(menuFor);
            }}
          />
        )}
      </BottomSheet>
      <LeaveDialog
        open={leaving !== null}
        inactive={leaving?.plot.inactive ?? false}
        loading={leave.isPending}
        onCancel={() => setLeaving(null)}
        onConfirm={() => {
          if (!leaving) return;
          const target = { conversationId: leaving.conversationId, plotId: leaving.plot.id };
          leave.mutate(target, {
            onSuccess: () => forget(target.plotId),
            onSettled: () => setLeaving(null),
          });
        }}
      />
      <BottomTabs current="chats" />
    </main>
  );
}
